import { X, CheckCircle2, AlertTriangle, AlertCircle, Info, ShieldCheck } from 'lucide-react';
import { getSeverityColor, getIssueTypeLabel } from '../../utils/modelValidator';

const ModelValidatorModal = ({ isOpen, onClose, validationResult, onSelectEntity }) => {
  if (!isOpen || !validationResult) return null;

  const { issues, summary, passed } = validationResult;

  // Group issues by severity
  const criticalIssues = issues.filter(i => i.severity === 'critical');
  const warningIssues = issues.filter(i => i.severity === 'warning');
  const infoIssues = issues.filter(i => i.severity === 'info');

  const handleEntityClick = (entityId) => {
    if (onSelectEntity && entityId) {
      onSelectEntity(entityId);
      onClose();
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle size={20} />;
      case 'warning':
        return <AlertTriangle size={20} />;
      case 'info':
        return <Info size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  const renderIssueCard = (issue, index) => {
    const colors = getSeverityColor(issue.severity);
    
    return (
      <div
        key={index}
        className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}
      >
        <div className="flex items-start gap-3">
          <div className={colors.icon}>
            {getSeverityIcon(issue.severity)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-semibold uppercase ${colors.text}`}>
                {getIssueTypeLabel(issue.type)}
              </span>
              {issue.count && (
                <span className={`text-xs px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                  {issue.count} {issue.count === 1 ? 'item' : 'items'}
                </span>
              )}
              {issue.similarity && (
                <span className={`text-xs px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                  {issue.similarity}% similar
                </span>
              )}
            </div>
            <p className={`text-sm font-medium ${colors.text} mb-2`}>
              {issue.message}
            </p>
            
            {/* Entities */}
            {issue.entities && issue.entities.length > 0 && (
              <div className="space-y-1">
                {issue.entities.map((entity, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleEntityClick(entity.id)}
                    className={`text-xs px-2 py-1 rounded ${colors.bg} ${colors.text} hover:opacity-80 transition-opacity underline cursor-pointer`}
                  >
                    {entity.name}
                  </button>
                ))}
              </div>
            )}
            
            {/* Fields */}
            {issue.fields && issue.fields.length > 0 && (
              <div className="mt-2 text-xs ${colors.text}">
                <span className="font-medium">Fields: </span>
                <span>{issue.fields.join(', ')}</span>
              </div>
            )}
            
            {/* Relationship */}
            {issue.relationship && (
              <div className="mt-2 text-xs ${colors.text}">
                <span className="font-medium">Relationship: </span>
                <span>{issue.relationship.label}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${passed ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
              <ShieldCheck size={24} className={passed ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Model Quality Report
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {passed ? 'No critical issues found' : 'Issues detected that need attention'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Summary */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {summary.total}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Total Issues
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                {summary.critical}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Critical
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                {summary.warning}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Warnings
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {summary.info}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Info
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {summary.total === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle2 size={64} className="text-green-500 dark:text-green-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Excellent! No Issues Found
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                Your model passed all quality checks. All entities have primary keys, fields, 
                proper types, and relationships are well-defined.
              </p>
            </div>
          ) : (
            <>
              {/* Critical Issues */}
              {criticalIssues.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Critical Issues ({criticalIssues.length})
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    These issues must be fixed before the model can be considered valid.
                  </p>
                  <div className="space-y-3">
                    {criticalIssues.map((issue, index) => renderIssueCard(issue, `critical-${index}`))}
                  </div>
                </div>
              )}

              {/* Warning Issues */}
              {warningIssues.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={20} className="text-yellow-600 dark:text-yellow-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Warnings ({warningIssues.length})
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    These issues should be reviewed and addressed when possible.
                  </p>
                  <div className="space-y-3">
                    {warningIssues.map((issue, index) => renderIssueCard(issue, `warning-${index}`))}
                  </div>
                </div>
              )}

              {/* Info Issues */}
              {infoIssues.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Info size={20} className="text-blue-600 dark:text-blue-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Recommendations ({infoIssues.length})
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    These are suggestions to improve model documentation and organization.
                  </p>
                  <div className="space-y-3">
                    {infoIssues.map((issue, index) => renderIssueCard(issue, `info-${index}`))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {summary.total > 0 ? (
              <span>Click on entity names to navigate to them</span>
            ) : (
              <span>Keep up the good work!</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModelValidatorModal;
